import type { Attachment } from '@medplum/fhirtypes'
import { formatDate } from '@seen/core/dates'
import type { ExtendedCoverage } from '@seen/core/models/coverage'
import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Button } from '@seen/ui'
import {
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconClock,
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconLink,
  IconPaperclip,
  IconUnlink,
  IconUpload,
} from '@tabler/icons-react'
import { type JSX, type ReactNode, useState } from 'react'
import { AttachmentCard } from '#src/components/attachments/AttachmentCard.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuSeparator } from '#src/components/core/dropdown-menu/DropdownMenuSeparator.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { UploadDocumentButton } from '#src/components/document-reference/UploadDocumentButton.tsx'

interface CoverageCardProps {
  title: ReactNode
  description: string
  isComplete: boolean
  coverage: ExtendedCoverage
  documents: ExtendedDocumentReference[]
  onUpload: (attachments: Attachment[]) => void
  onLinkDocuments: () => void
  onUnlinkDocument: (docId: string) => void
  onEditCoverage: () => void
  onUpdateDocumentTitle: (
    docId: string,
    attachmentIndex: number,
    title: string
  ) => void
}

export function CoverageCard({
  title,
  description,
  isComplete,
  coverage,
  documents,
  onUpload,
  onLinkDocuments,
  onUnlinkDocument,
  onEditCoverage,
  onUpdateDocumentTitle,
}: CoverageCardProps): JSX.Element {
  const [expandedDocuments, setExpandedDocuments] = useState<Set<string>>(
    new Set()
  )

  const toggleDocumentExpansion = (docId: string): void => {
    setExpandedDocuments((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(docId)) {
        newSet.delete(docId)
      } else {
        newSet.add(docId)
      }
      return newSet
    })
  }

  return (
    <Card className="overflow-hidden">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-1">
              {isComplete ? (
                <IconCircleCheck className="h-5 w-5 text-green-600" />
              ) : (
                <IconClock className="h-5 w-5 text-gray-400" />
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className="text-sm text-gray-600">{description}</p>
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                {coverage.subscriberId && (
                  <span>Member ID: {coverage.subscriberId}</span>
                )}
                {coverage.startDate && (
                  <span>Start: {formatDate(coverage.startDate)}</span>
                )}
                {coverage.endDate && (
                  <span>End: {formatDate(coverage.endDate)}</span>
                )}
              </div>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <IconDotsVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEditCoverage}>
                <IconEdit className="mr-2 size-4" />
                Edit Coverage
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLinkDocuments}>
                <IconLink className="mr-2 size-4" />
                Link Documents
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {documents.length === 0 && (
          <div className="space-y-2">
            <UploadDocumentButton onUpload={onUpload}>
              {(props) => (
                <div
                  {...props}
                  className="flex h-16 cursor-pointer items-center justify-center border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <IconUpload className="h-5 w-5 text-gray-400" />
                    <p className="text-sm text-gray-600">
                      Upload proof of medicare coverage or drop files here
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        onLinkDocuments()
                      }}
                      className="ml-4"
                    >
                      Link Documents
                    </Button>
                  </div>
                </div>
              )}
            </UploadDocumentButton>
          </div>
        )}

        {documents.length > 0 && (
          <div className="space-y-2">
            {documents.map((doc) => {
              const hasMultipleAttachments =
                doc.attachments && doc.attachments.length > 1
              const isExpanded = expandedDocuments.has(doc.id)

              return (
                <div
                  key={doc.id}
                  className="overflow-hidden rounded-lg border border-gray-200 bg-white"
                >
                  <div className="">
                    {doc.attachments?.[0] && (
                      <AttachmentCard
                        attachment={doc.attachments[0]}
                        onTitleChange={(title) =>
                          onUpdateDocumentTitle(doc.id, 0, title)
                        }
                      >
                        <div className="flex items-center gap-1">
                          {hasMultipleAttachments && (
                            <div className="mr-2 flex items-center">
                              <IconPaperclip className="size-3 text-gray-400" />
                              <span className="text-xs text-gray-500">
                                {doc.attachments.length}
                              </span>
                            </div>
                          )}
                          {hasMultipleAttachments && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleDocumentExpansion(doc.id)}
                              className="shrink-0"
                            >
                              {isExpanded ? (
                                <IconChevronDown className="size-4" />
                              ) : (
                                <IconChevronRight className="size-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onUnlinkDocument(doc.id)}
                            className="shrink-0 hover:text-red-500"
                          >
                            <IconUnlink className="size-4" />
                          </Button>
                        </div>
                      </AttachmentCard>
                    )}

                    {hasMultipleAttachments && isExpanded && (
                      <div className="space-y-2 mt-3 pl-4">
                        <h5 className="mb-2 text-sm font-medium text-gray-700">
                          Additional Attachments
                        </h5>
                        {doc.attachments.slice(1).map((attachment, index) => (
                          <AttachmentCard
                            key={index}
                            attachment={attachment}
                            onTitleChange={(title) =>
                              onUpdateDocumentTitle(doc.id, index + 1, title)
                            }
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0"
                            >
                              <IconDownload className="size-4" />
                            </Button>
                          </AttachmentCard>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Card>
  )
}
